/**
 * Notifications tab — MEMBER-self-serve Web Push subscription manager.
 * Slice 5 wires:
 *   - Browser-support detection (service worker + PushManager + Notif)
 *   - Subscribe flow: request permission → register `/service-worker.js`
 *     → fetch VAPID public key → `pushManager.subscribe` → POST
 *     subscription to runtime
 *   - Unsubscribe flow: server-side `unsubscribePush(id)` (with the
 *     runtime's id, matched to the current browser by endpoint) +
 *     browser-side `subscription.unsubscribe()`
 *   - "Send test push" button (POST `/config/push/test`) — fires real
 *     VAPID delivery (P5.A.2.b)
 *
 * Deferred (will land with Slice 7's noVNC pane, paired in one commit):
 *   - Service-worker → runtime event-relay (`delivered`/`clicked`/
 *     `dismissed`) — requires a postMessage bridge to hand the SW an
 *     auth token.
 *   - Recent events list (`GET /push-events/recent`) — the runtime
 *     hasn't shipped that HTTP route yet (events are agent-tool-only
 *     for now via `lx_recent_push_events`).
 *
 * Kill-switch: `userSettingsPolicy.push === false` locks the whole tab
 * (cannot subscribe; existing subscriptions stay visible for
 * preservation §2 — user can still unsubscribe).
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertCircle,
  Bell,
  BellOff,
  CheckCircle2,
  Loader2,
  Lock,
  Send,
} from "lucide-react";
import { Card } from "@/components/ui/Card";
import { useToastStore } from "@/stores/useToastStore";
import {
  getPushVapidPublicKey,
  LxOpenclawConfigError,
  sendTestPush,
  subscribePush,
  unsubscribePush,
  type PushSubscription as RuntimePushSubscription,
} from "@/api/lx-openclaw-config";
import { isDisabledByOrg } from "@/lib/openclaw-policy";
import { cn } from "@/lib/utils";
import { useOpenclawSettings } from "./OpenclawSettingsTabs";

// ─── Helpers ──────────────────────────────────────────────────────────

/** Convert the runtime's VAPID public key (base64-url string) into the
 *  Uint8Array `PushManager.subscribe({ applicationServerKey })` expects.
 *  Standard recipe — keep inline so the SW + page don't share a module. */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function detectBrowserSupport(): boolean {
  if (typeof window === "undefined") return false;
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

interface CurrentSubscriptionState {
  /** Server-side subscription id, matched by endpoint. `null` when the
   *  browser has a PushSubscription but the runtime hasn't recorded it
   *  (rare — implies a desync; the user can unsubscribe browser-side). */
  serverId: string | null;
  endpoint: string;
}

// ─── Component ────────────────────────────────────────────────────────

export function NotificationsTab() {
  const { t } = useTranslation();
  const { config, refresh, notifyRouteNotFound } = useOpenclawSettings();
  const addToast = useToastStore((s) => s.addToast);
  const lockedByOrg = isDisabledByOrg(config.org, "push");

  const supported = detectBrowserSupport();
  const [permission, setPermission] = useState<NotificationPermission | null>(
    null,
  );
  const [current, setCurrent] = useState<CurrentSubscriptionState | null>(null);
  const [busy, setBusy] = useState<"subscribe" | "unsubscribe" | "test" | null>(
    null,
  );

  // Probe the current browser-side state on mount + after every relevant
  // server-side change (subscribe/unsubscribe via refresh()). Matches the
  // browser's PushSubscription endpoint against the runtime's recorded
  // `user.push.subscriptions[]` to find this device's server-side id.
  useEffect(() => {
    if (!supported) return;
    let cancelled = false;
    (async () => {
      try {
        setPermission(Notification.permission);
        const reg = await navigator.serviceWorker.getRegistration();
        const sub = await reg?.pushManager.getSubscription();
        if (cancelled) return;
        if (!sub) {
          setCurrent(null);
          return;
        }
        const serverRows: RuntimePushSubscription[] =
          config.user?.push?.subscriptions ?? [];
        const match = serverRows.find((r) => r.endpoint === sub.endpoint);
        setCurrent({
          endpoint: sub.endpoint,
          serverId: match?.id ?? null,
        });
      } catch {
        if (!cancelled) setCurrent(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [supported, config.user?.push?.subscriptions]);

  async function onSubscribe() {
    if (busy || lockedByOrg) return;
    setBusy("subscribe");
    try {
      // 1) Permission. Don't store a "denied" result aggressively — the
      // browser may surface a permanent-block UI; the user has to flip
      // it back in site settings.
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        addToast({
          kind: "error",
          text: t(
            perm === "denied"
              ? "settings.openclaw.notifications.permissionDenied"
              : "settings.openclaw.notifications.permissionDismissed",
          ),
          autoDismissMs: 6000,
        });
        return;
      }
      // 2) Register the SW (idempotent if already registered).
      const reg = await navigator.serviceWorker.register("/service-worker.js");
      // 3) Mint a subscription against the runtime's real VAPID key.
      const vapidKey = await getPushVapidPublicKey();
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        // PushManager.subscribe's BufferSource type rejects the
        // default `Uint8Array<ArrayBufferLike>` because that union
        // includes SharedArrayBuffer. Our buffer is always a plain
        // ArrayBuffer (atob → new Uint8Array), so the assertion is
        // safe and the spec accepts it at runtime.
        applicationServerKey: urlBase64ToUint8Array(vapidKey).buffer as ArrayBuffer,
      });
      const subJson = sub.toJSON();
      if (!subJson.endpoint || !subJson.keys) {
        throw new Error("PushSubscription serialization missing fields");
      }
      // 4) Hand the subscription to the runtime.
      await subscribePush({
        endpoint: subJson.endpoint,
        keys: {
          auth: subJson.keys.auth,
          p256dh: subJson.keys.p256dh,
        },
        deviceLabel:
          typeof navigator !== "undefined"
            ? deriveDeviceLabel(navigator.userAgent)
            : undefined,
      });
      await refresh();
      addToast({
        kind: "info",
        text: t("settings.openclaw.notifications.subscribed"),
        autoDismissMs: 4000,
      });
    } catch (err) {
      handleErr(err, "subscribe");
    } finally {
      setBusy(null);
    }
  }

  async function onUnsubscribe() {
    if (busy || !current) return;
    setBusy("unsubscribe");
    try {
      // Server-side first — if the runtime is unreachable we'd rather
      // keep the browser sub around than orphan a server row. The
      // server endpoint is idempotent for unknown ids.
      if (current.serverId) {
        await unsubscribePush(current.serverId);
      }
      // Browser-side. May throw if no registration; tolerate.
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        const sub = await reg?.pushManager.getSubscription();
        await sub?.unsubscribe();
      } catch {
        /* tolerate */
      }
      await refresh();
      setCurrent(null);
      addToast({
        kind: "info",
        text: t("settings.openclaw.notifications.unsubscribed"),
        autoDismissMs: 4000,
      });
    } catch (err) {
      handleErr(err, "unsubscribe");
    } finally {
      setBusy(null);
    }
  }

  async function onTest() {
    if (busy || !current?.serverId) return;
    setBusy("test");
    try {
      await sendTestPush(current.serverId);
      addToast({
        kind: "info",
        text: t("settings.openclaw.notifications.testSent"),
        autoDismissMs: 6000,
      });
    } catch (err) {
      handleErr(err, "test");
    } finally {
      setBusy(null);
    }
  }

  function handleErr(err: unknown, action: "subscribe" | "unsubscribe" | "test") {
    if (err instanceof LxOpenclawConfigError) {
      if (err.routeNotFound) {
        notifyRouteNotFound("notifications");
        addToast({
          kind: "error",
          text: t("settings.openclaw.upgradeRequired.toast"),
          autoDismissMs: 8000,
        });
        return;
      }
      if (err.code === "USER_SETTING_DISABLED_BY_ORG") {
        void refresh();
        addToast({
          kind: "error",
          text: t("settings.openclaw.disabledByOrgRace"),
          autoDismissMs: 6000,
        });
        return;
      }
    }
    addToast({
      kind: "error",
      text: t(
        action === "subscribe"
          ? "settings.openclaw.notifications.subscribeFailed"
          : action === "unsubscribe"
            ? "settings.openclaw.notifications.unsubscribeFailed"
            : "settings.openclaw.notifications.testFailed",
      ),
      autoDismissMs: 6000,
    });
  }

  return (
    <Card variant="surface" data-testid="openclaw-notifications-tab">
      <div className="flex items-center gap-2">
        <Bell size={18} className="text-cream-500" />
        <h3 className="font-serif-display text-display-sm text-coffee-900 dark:text-cream-100">
          {t("settings.openclaw.notifications.title")}
        </h3>
        {lockedByOrg && (
          <span
            data-testid="openclaw-notifications-locked"
            title={t("settings.openclaw.lockedByOrgTooltip")}
            className="inline-flex items-center gap-1 rounded bg-cream-100 px-1.5 py-0.5 text-[10px] font-medium text-coffee-500 dark:bg-coffee-800 dark:text-cream-400"
          >
            <Lock size={9} />
            {t("settings.openclaw.lockedByOrg")}
          </span>
        )}
      </div>
      <p className="mt-3 text-body-sm text-coffee-600 dark:text-cream-400">
        {t("settings.openclaw.notifications.caption")}
      </p>

      {!supported ? (
        <p
          data-testid="openclaw-notifications-unsupported"
          className="mt-5 flex items-start gap-2 text-body-sm text-coffee-500 dark:text-cream-500"
        >
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          {t("settings.openclaw.notifications.unsupported")}
        </p>
      ) : (
        <div className="mt-5 flex flex-col gap-4">
          {/* Status pill — quick read of where things stand. */}
          <StatusRow
            permission={permission}
            current={current}
            lockedByOrg={lockedByOrg}
          />

          {/* Action buttons */}
          <div className="flex flex-wrap items-center gap-2">
            {!current ? (
              <button
                type="button"
                data-testid="openclaw-notifications-subscribe"
                disabled={lockedByOrg || busy !== null}
                onClick={() => void onSubscribe()}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-body-sm font-medium transition-colors",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40 focus-visible:ring-offset-2 focus-visible:ring-offset-cream-50 dark:focus-visible:ring-offset-coffee-900",
                  lockedByOrg || busy !== null
                    ? "cursor-not-allowed border-cream-200/60 text-cream-500 dark:border-coffee-700/60"
                    : "border-coffee-800 bg-coffee-800 text-cream-50 hover:bg-coffee-900 hover:border-coffee-900 dark:border-cream-100 dark:bg-cream-100 dark:text-coffee-900 dark:hover:bg-cream-50 dark:hover:border-cream-50",
                )}
              >
                {busy === "subscribe" ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <Bell size={13} />
                )}
                {busy === "subscribe"
                  ? t("settings.openclaw.notifications.subscribingButton")
                  : t("settings.openclaw.notifications.subscribeButton")}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  data-testid="openclaw-notifications-test"
                  disabled={busy !== null || !current.serverId}
                  title={
                    !current.serverId
                      ? t("settings.openclaw.notifications.noServerIdHint")
                      : undefined
                  }
                  onClick={() => void onTest()}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-body-sm font-medium transition-colors",
                    busy !== null || !current.serverId
                      ? "cursor-not-allowed border-cream-200/60 text-cream-500 dark:border-coffee-700/60"
                      : "border-coffee-800 bg-coffee-800 text-cream-50 hover:bg-coffee-900 hover:border-coffee-900 dark:border-cream-100 dark:bg-cream-100 dark:text-coffee-900 dark:hover:bg-cream-50 dark:hover:border-cream-50",
                  )}
                >
                  {busy === "test" ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : (
                    <Send size={13} />
                  )}
                  {busy === "test"
                    ? t("settings.openclaw.notifications.sendingButton")
                    : t("settings.openclaw.notifications.testButton")}
                </button>
                <button
                  type="button"
                  data-testid="openclaw-notifications-unsubscribe"
                  disabled={busy !== null}
                  onClick={() => void onUnsubscribe()}
                  className="inline-flex items-center gap-1.5 rounded-md border border-cream-200/60 px-3 py-1.5 text-body-sm font-medium text-coffee-600 transition-colors hover:bg-cream-50 hover:text-coffee-900 disabled:cursor-not-allowed disabled:opacity-60 dark:border-coffee-700/60 dark:text-cream-400 dark:hover:bg-coffee-800/60 dark:hover:text-cream-100"
                >
                  {busy === "unsubscribe" ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : (
                    <BellOff size={13} />
                  )}
                  {busy === "unsubscribe"
                    ? t("settings.openclaw.notifications.unsubscribingButton")
                    : t("settings.openclaw.notifications.unsubscribeButton")}
                </button>
              </>
            )}
          </div>

          <p className="text-caption text-coffee-500 dark:text-cream-500">
            {t("settings.openclaw.notifications.recentEventsSoon")}
          </p>
        </div>
      )}
    </Card>
  );
}

// ─── Status row ───────────────────────────────────────────────────────

function StatusRow({
  permission,
  current,
  lockedByOrg,
}: {
  permission: NotificationPermission | null;
  current: CurrentSubscriptionState | null;
  lockedByOrg: boolean;
}) {
  const { t } = useTranslation();
  let label: string;
  let tone: "ok" | "warn" | "neutral";
  let Icon = Bell;
  if (lockedByOrg) {
    label = t("settings.openclaw.notifications.statusLocked");
    tone = "warn";
    Icon = Lock;
  } else if (current) {
    label = t("settings.openclaw.notifications.statusActive");
    tone = "ok";
    Icon = CheckCircle2;
  } else if (permission === "denied") {
    label = t("settings.openclaw.notifications.statusBlocked");
    tone = "warn";
    Icon = AlertCircle;
  } else {
    label = t("settings.openclaw.notifications.statusOff");
    tone = "neutral";
    Icon = BellOff;
  }
  return (
    <div
      data-testid="openclaw-notifications-status"
      className={cn(
        "inline-flex items-center gap-1.5 self-start rounded-md px-2 py-1 text-xs font-medium",
        tone === "ok"
          ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
          : tone === "warn"
            ? "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
            : "bg-cream-100 text-coffee-600 dark:bg-coffee-800 dark:text-cream-400",
      )}
    >
      <Icon size={12} />
      {label}
    </div>
  );
}

// ─── Device label heuristic ──────────────────────────────────────────

/** Pull a short, human-meaningful device label out of a user-agent
 *  string. Falls back to "Browser" — never throws. The runtime stores
 *  whatever we send so the user can tell devices apart in the list. */
function deriveDeviceLabel(ua: string): string {
  const lower = ua.toLowerCase();
  // Order matters — most specific first.
  if (/edg\//i.test(ua)) return "Edge";
  if (/chrome\//i.test(ua) && !/chromium/i.test(ua)) return "Chrome";
  if (/firefox\//i.test(ua)) return "Firefox";
  if (/safari\//i.test(ua) && !/chrome/i.test(lower)) return "Safari";
  if (/opera|opr\//i.test(ua)) return "Opera";
  return "Browser";
}
